import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { OPEN_CONSENT_EVENT } from "../../CookieConsentRoot.jsx";
import { SOCIAL_LINKS } from "../shared/socialLinks.jsx";

const LEGAL_PATH = {
  impressum: { de: "impressum", en: "legal-notice" },
  datenschutz: { de: "datenschutz", en: "privacy" },
};
const LARP_PATH = { de: "larp-erleben", en: "experience-larp" };
const ABOUT_PATH = { de: "ueber-uns", en: "about" };
const JUGENDSCHUTZ_PATH = { de: "jugendschutzkonzept", en: "youth-protection-concept" };
const ORGAS_PATH = { de: "fuer-orgas", en: "for-organizers" };
const SPENDEN_PATH = { de: "spenden", en: "donate" };
const SUPPORT_PATH = { de: "unterstuetzen", en: "support" };
const KONTAKT_PATH = { de: "kontakt", en: "contact" };

const columnTitleStyle = {
  fontFamily: "var(--font-serif)",
  fontStyle: "italic",
  fontSize: "14px",
  color: "var(--color-gold)",
  margin: 0,
};

const linkRowStyle = {
  fontSize: "13px",
  color: "var(--color-cream-soft)",
};

export default function Footer({ lang }) {
  const { t } = useTranslation();

  return (
    <footer
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: "40px",
        padding: "48px 40px",
        background: "var(--color-bg)",
        borderTop: "1px solid rgba(254,250,239,0.08)",
        flexWrap: "wrap",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "14px", maxWidth: "34ch" }}>
        <img src="/assets/brand/logo-mark-cream.svg" alt="Tales&Legends" width={63} height={40} style={{ height: "40px", width: "auto" }} />
        {/*
          Both lines below fold everything into one string/expression per
          text run — a mix of literal text and {expr} children on the same
          line compiles to multiple adjacent JSX text nodes, which a live-
          browser DOM snapshot (this app's prerender step) merges into one
          continuous run with no way to mark where React's own
          renderToString() would have split them, so hydration misreads the
          merged text as not matching the first expected fragment.
        */}
        <p style={{ fontSize: "12px", color: "var(--color-cream-faint)", margin: 0 }}>
          {`© ${new Date().getFullYear()} Tales&Legends gUG — ${t("footer.rights")}`}
        </p>
        <p style={{ fontSize: "12px", color: "var(--color-cream-faint)", margin: 0 }}>
          {"Reiboldschachtring 28, 01705 Freital — "}
          <Link to={`/${lang}/${KONTAKT_PATH[lang]}`} style={{ color: "inherit", textDecoration: "underline" }}>
            {t("footer.moreInfo")}
          </Link>
        </p>
      </div>

      <div style={{ display: "flex", gap: "56px", flexWrap: "wrap" }}>
        <nav aria-label={t("footer.columnSocial")} style={{ display: "flex", flexDirection: "column", gap: "9px" }}>
          <p style={columnTitleStyle}>{t("footer.columnSocial")}</p>
          {SOCIAL_LINKS.map((social) => (
            <a key={social.key} href={social.url} target="_blank" rel="noopener noreferrer" style={linkRowStyle}>
              {social.label}
            </a>
          ))}
        </nav>

        <nav aria-label={t("footer.columnMitmachen")} style={{ display: "flex", flexDirection: "column", gap: "9px" }}>
          <p style={columnTitleStyle}>{t("footer.columnMitmachen")}</p>
          <Link to={`/${lang}/${SPENDEN_PATH[lang]}`} style={linkRowStyle}>
            {t("footer.linkProjekteFoerdern")}
          </Link>
          <Link to={`/${lang}/events`} style={linkRowStyle}>
            {t("footer.linkEventsBesuchen")}
          </Link>
          <Link to={`/${lang}/${ABOUT_PATH[lang]}/${ORGAS_PATH[lang]}`} style={linkRowStyle}>
            {t("footer.linkEventsVeranstalten")}
          </Link>
          <Link to={`/${lang}/${SUPPORT_PATH[lang]}`} style={linkRowStyle}>
            {t("nav.unterstuetzen")}
          </Link>
        </nav>

        <nav aria-label={t("nav.larpErleben")} style={{ display: "flex", flexDirection: "column", gap: "9px" }}>
          <p style={columnTitleStyle}>{t("nav.larpErleben")}</p>
          <Link to={`/${lang}/${LARP_PATH[lang]}`} style={linkRowStyle}>
            {t("footer.linkLarpEinsteiger")}
          </Link>
          <Link to={`/${lang}/${LARP_PATH[lang]}/${lang === "de" ? "glossar" : "glossary"}`} style={linkRowStyle}>
            {t("footer.linkGlossar")}
          </Link>
          <Link to={`/${lang}/${LARP_PATH[lang]}/faq`} style={linkRowStyle}>
            {t("footer.linkFaq")}
          </Link>
        </nav>

        <nav aria-label="Legal links" style={{ display: "flex", flexDirection: "column", gap: "9px" }}>
          <p style={columnTitleStyle}>{t("footer.columnOrganisation")}</p>
          <Link to={`/${lang}/${ABOUT_PATH[lang]}`} style={linkRowStyle}>
            {t("nav.ueberUns")}
          </Link>
          <Link to={`/${lang}/${ABOUT_PATH[lang]}/${JUGENDSCHUTZ_PATH[lang]}`} style={linkRowStyle}>
            {t("about.jugendschutz.title")}
          </Link>
          <Link to={`/${lang}/${KONTAKT_PATH[lang]}`} style={linkRowStyle}>
            {t("footer.kontakt")}
          </Link>
          <Link to={`/${lang}/${LEGAL_PATH.impressum[lang]}`} style={linkRowStyle}>
            {t("footer.impressum")}
          </Link>
          <Link to={`/${lang}/${LEGAL_PATH.datenschutz[lang]}`} style={linkRowStyle}>
            {t("footer.datenschutz")}
          </Link>
          <button
            type="button"
            // Used to clear the consent cookie and reload, which was the
            // only way a single accept/decline value could be revisited.
            // Now that each category has its own toggle, this reopens the
            // banner with the stored choice pre-ticked instead of throwing
            // it away — and a withdrawal still reloads, from inside
            // CookieConsentRoot's save() where it belongs.
            onClick={() => window.dispatchEvent(new Event(OPEN_CONSENT_EVENT))}
            // `font: "inherit"` is a shorthand that resets font-size (and
            // every other font sub-property) regardless of what
            // linkRowStyle's own fontSize says — Chrome's CSSOM recognizes
            // that redundancy and drops the now-meaningless font-size when
            // serializing outerHTML, but React's plain JS-object style
            // serialization keeps both, which then reads as a hydration
            // mismatch. Overriding fontSize to undefined here matches what
            // the shorthand already does visually, and keeps the two in sync.
            style={{ ...linkRowStyle, fontSize: undefined, background: "none", border: "none", padding: 0, textAlign: "left", font: "inherit", cursor: "pointer" }}
          >
            {t("footer.cookieSettings")}
          </button>
        </nav>
      </div>
    </footer>
  );
}
